import { z } from "zod/v4";
import { parseApiErrorBody } from "./api-errors";

/** Legacy Volume API error format */
const legacyErrorObject = z.object({
  statusCode: z.number(),
  statusName: z.string(),
  errorMessage: z.string(),
  traceId: z.string().optional(),
  source: z.string(),
});
type VolumeApiErrorObject = z.infer<typeof legacyErrorObject>;

/** New API error format (_tag + message) */
const newApiErrorObject = z.object({
  _tag: z.string(),
  message: z.string(),
});
type NewApiErrorObject = z.infer<typeof newApiErrorObject>;

const isLegacyVolumeError = (error: unknown): error is VolumeApiErrorObject =>
  legacyErrorObject.safeParse(error).success;

const isNewApiError = (error: unknown): error is NewApiErrorObject =>
  newApiErrorObject.safeParse(error).success;

const isVolumeApiError = (
  error: unknown,
): error is VolumeApiErrorObject | NewApiErrorObject =>
  isLegacyVolumeError(error) || isNewApiError(error);

export class VolumeApiError extends Error {
  public statusCode: number;
  public statusName: string;
  public errorMessage: string;
  public source: string;
  public traceId: string | undefined;
  /** Error type tag from new API format. Use for branching. */
  public tag: string | undefined;

  constructor(error: unknown) {
    super();
    if (!isVolumeApiError(error)) {
      throw new Error("Invalid volume api error");
    }
    if (isLegacyVolumeError(error)) {
      this.statusCode = error.statusCode;
      this.statusName = error.statusName;
      this.errorMessage = error.errorMessage;
      this.traceId = error.traceId;
      this.source = error.source;
      this.tag = undefined;
      this.message = error.errorMessage;
    } else {
      const parsed = parseApiErrorBody(error);
      this.errorMessage = parsed.message;
      this.message = parsed.message;
      this.statusCode = parsed.statusCode ?? 0;
      this.statusName = "";
      this.traceId = parsed.traceId as string | undefined;
      this.source = "";
      this.tag = parsed.tag;
    }
  }

  public static isVolumeApiError(
    error: unknown,
  ): error is VolumeApiErrorObject | NewApiErrorObject {
    return isVolumeApiError(error);
  }
}
